import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Modal,
  Radio,
  RadioGroup,
} from "@mui/material";
import * as S from "./styles";
import logo from "@/assets/svgs/logo.svg";
import { ReactComponent as SuccessSvg } from "@/assets/svgs/success-circle.svg";

import {
  MdOutlineAnalytics,
  MdOutlineCopyAll,
  MdOutlineGroups,
  MdOutlinePsychology,
} from "react-icons/md";
import { RiMoneyDollarCircleLine } from "react-icons/ri";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { AiOutlineArrowLeft } from "react-icons/ai";
import RoundInput from "@/components/RoundInput";
import { FutButton } from "@/components/FutButton";
import * as yup from "yup";
import {
  PASSWORD_MATCH,
  REQUIRED_FIELD,
  STRONG_PASSWORD,
  VALID_PHONE,
  VALID_CPF,
  VALID_EMAIL,
  has_accepted_terms,
} from "@/constants/yupErrors";
import { SubmitHandler, useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { passwordRegex, phoneRegex } from "@/utils/regex";
import { cpfIsValid } from "@/utils/cpfValidator";
import PatternInput from "@/components/PatternInput";
import { api } from "@/services/api";
import { toast } from "react-toastify";
import { catchError } from "@/utils/catchError";
import { plans, plansPrices } from "@/constants/plans";
import PlansChooser from "@/components/PlansChooser";
import { FaPix } from "react-icons/fa6";
import { BsFillCreditCard2BackFill } from "react-icons/bs";
import QRCode from "react-qr-code";
import { getCookie, setCookie } from "@/utils/cookies";
import { IoClose } from "react-icons/io5";
import { useAuth } from "@/context/AuthContext";

const registerSchema = yup.object({
  name: yup.string().required(REQUIRED_FIELD),
  surname: yup.string().required(REQUIRED_FIELD),
  email: yup.string().required(REQUIRED_FIELD).trim().email(VALID_EMAIL),
  phone: yup.string().required(REQUIRED_FIELD).matches(phoneRegex, VALID_PHONE),
  cpf: yup
    .string()
    .required(REQUIRED_FIELD)
    .test(
      "is-cpf-valid",
      () => VALID_CPF,
      (value) => cpfIsValid(value)
    ),
  password: yup
    .string()
    .required(REQUIRED_FIELD)
    .matches(passwordRegex, STRONG_PASSWORD),
  repeat_password: yup
    .string()
    .required(REQUIRED_FIELD)
    .oneOf([yup.ref("password")], PASSWORD_MATCH),
  // has_accepted_terms: yup.boolean().required(has_accepted_terms)
});
type RegisterInputs = yup.InferType<typeof registerSchema> & {
  parent_user_id?: number | "";
  payment?: string;
};

export function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [planType, setPlanType] = useState<keyof typeof plans>("monthly");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTermServicesOpen, setIsTermsServicesOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sellerName, setSellerName] = useState("");

  const [searchParams] = useSearchParams();
  const affiliatedLink = searchParams.get("af");
  const { signIn } = useAuth();
  useEffect(() => {
    if (
      !affiliatedLink ||
      !parseInt(affiliatedLink) ||
      parseInt(affiliatedLink) <= 0
    ) {
      navigate("/login");
      toast.error(
        "Você precisa do link de um de nossos parceiros, confira o ID na URL e atualize a página",
        { autoClose: 15000 }
      );
      return;
    }

    if (affiliatedLink) {
      localStorage.setItem("affiliatedId", affiliatedLink);
    }

    async function getSellerName() {
      try {
        const response = await api.get(`/user/getName/${affiliatedLink}`);
        setSellerName(response.data.name);
      } catch (e) {
        setSellerName("-1");
      }
    }

    getSellerName();
  }, []);

  const registerForm = useForm<RegisterInputs>({
    resolver: yupResolver(registerSchema),
    defaultValues: {
      cpf: "",
      email: "",
      name: "",
      surname: "",
      password: "",
      phone: "",
      repeat_password: "",
      payment: "CREDIT_CARD",
      // has_accepted_terms: false
    },
  });

  const registerErrors = registerForm.formState.errors;

  const changeStep = (toStep: "prev" | "next") => {
    if (containerRef.current) {
      containerRef.current.style.opacity = "0";

      setTimeout(() => {
        if (toStep === "next") {
          setStep((prev) => prev + 1);
        }

        if (toStep === "prev") {
          setStep((prev) => prev - 1);
        }
        containerRef.current!.style.opacity = "1";
        setIsLoading(false);
      }, 400);
    }
  };

  const planHandler = (plan: keyof typeof plans) => {
    setIsLoading(true);

    setPlanType(plan);

    changeStep("next");
  };

  const registerSubmit: SubmitHandler<RegisterInputs> = async (data) => {
    try {
      setIsLoading(true);
      // if (!data.has_accepted_terms) {
      //   setIsLoading(false);
      //   return toast.warn('Para seguir em frente é necessário concordar com nossos termos de uso');
      // }

      const { surname, payment, ...formData } = data;
      const parentId = localStorage.getItem("affiliatedId");
      formData.name = `${formData.name} ${data.surname}`;
      formData.cpf = formData.cpf.replace(/[^\d]/g, "");

      parentId
        ? (formData.parent_user_id = parseInt(parentId))
        : delete formData.parent_user_id;

      const response = await api.post("/user/create", {
        ...formData,
        has_accepted_terms: true,
      });
      const dataResponse: {
        userId: number;
        name: string;
        email: string;
        phone: string;
        password: string;
      } = response.data;
      toast.success("usuário cadastrado com sucesso!");
      await signIn({ email: dataResponse.email, password: dataResponse.password });
      navigate("/dashboard")
    } catch (e) {
      setIsModalOpen(false);
      setIsLoading(false);
      const message = catchError(e);
      if (typeof message === "string") {
        toast.error(message);
        return;
      }
      toast.error(
        "Ocorreu um erro inesperado ao enviar sua solicitação, possivelmente você já pode está registrado",
        { autoClose: 40000 }
      );
      return;
    }
  };

  const registerParts = [
    // {
    //   components: (
    //     <Grid container justifyContent="center" my={3} gap="24px">
    //       {sellerName === "-1" ? (
    //         <></>
    //       ) : (
    //         <Grid item sm={12} display="flex" justifyContent="center">
    //           <S.IndicationContainer>
    //             <h2>Você foi indicado por:</h2>
    //             <p>{sellerName || "Carregando..."}</p>
    //             <h2>Bem vindo a familia Futscore!</h2>
    //           </S.IndicationContainer>
    //         </Grid>
    //       )}
    //       <Grid item lg={3}>
    //         <S.BenefitsWrapper>
    //           <h2>O que você vai ganhar</h2>

    //           <S.BenefitsContainer>
    //             <div>
    //               <MdOutlineAnalytics />
    //             </div>
    //             <p>Métricas de jogos ao vivo.</p>
    //           </S.BenefitsContainer>

    //           <S.BenefitsContainer>
    //             <div>
    //               <MdOutlinePsychology />
    //             </div>
    //             <p>
    //               Acesso à Futscore com sinais de surebet descobertos pela IA.
    //             </p>
    //           </S.BenefitsContainer>

    //           <S.BenefitsContainer>
    //             <div>
    //               <MdOutlineGroups />
    //             </div>
    //             <p>Permissão de revenda como afiliado multinível.</p>
    //           </S.BenefitsContainer>

    //           <S.BenefitsContainer>
    //             <div>
    //               <RiMoneyDollarCircleLine />
    //             </div>
    //             <p>Saques semanais.</p>
    //           </S.BenefitsContainer>
    //         </S.BenefitsWrapper>
    //       </Grid>
    //       <Grid item lg={3}>
    //         <PlansChooser
    //           planHandler={(plan) => planHandler(plan)}
    //           disabled={isLoading}
    //         />
    //       </Grid>
    //     </Grid>
    //   ),
    // },
    {
      components: (
        <S.RegisterWrapper>
          <S.RegisterContainer>
            <S.RegisterHeader>
              <div>
                {/* <button onClick={() => changeStep("prev")}>
                  <AiOutlineArrowLeft />
                </button> */}
                <p>Criar Conta</p>
              </div>
              {/* <p>Passo 1 de 2</p> */}
            </S.RegisterHeader>

            <form onSubmit={registerForm.handleSubmit(registerSubmit)}>
              <S.RegisterBody>
                <S.JoinInputs>
                  <RoundInput
                    inputName="name"
                    control={registerForm.control}
                    fullWidth
                    placeholder="Nome"
                    errorText={registerErrors.name}
                    topLabel="Nome"
                    size="small"
                    autoComplete="given-name"
                  />
                  <RoundInput
                    inputName="surname"
                    control={registerForm.control}
                    fullWidth
                    placeholder="Sobrenome"
                    errorText={registerErrors.surname}
                    topLabel="Sobrenome"
                    size="small"
                    autoComplete="additional-name"
                  />
                </S.JoinInputs>

                <RoundInput
                  inputName="email"
                  control={registerForm.control}
                  fullWidth
                  placeholder="email@email.com"
                  errorText={registerErrors.email}
                  topLabel="E-mail"
                  size="small"
                  autoComplete="email"
                />

                <PatternInput
                  inputName="phone"
                  control={registerForm.control}
                  fullWidth
                  format="(##) #####-####"
                  mask="_"
                  placeholder="(00) 00000-0000"
                  errorText={registerErrors.phone}
                  topLabel="Telefone"
                  size="small"
                  autoComplete="phone"
                />
                <PatternInput
                  inputName="cpf"
                  control={registerForm.control}
                  fullWidth
                  format="###.###.###-##"
                  mask="_"
                  placeholder="000000000-00"
                  errorText={registerErrors.cpf}
                  topLabel="CPF"
                  size="small"
                />

                <RoundInput
                  inputName="password"
                  control={registerForm.control}
                  fullWidth
                  placeholder="********"
                  errorText={registerErrors.password}
                  topLabel="Senha"
                  size="small"
                  type="password"
                  autoComplete="new-password"
                />
                <RoundInput
                  inputName="repeat_password"
                  control={registerForm.control}
                  fullWidth
                  placeholder="********"
                  errorText={registerErrors.repeat_password}
                  topLabel="Confirmar Senha"
                  size="small"
                  type="password"
                  autoComplete="new-password"
                />
              </S.RegisterBody>

              <Controller
                name="payment"
                control={registerForm.control}
                render={({ field }) => (
                  <RadioGroup {...field}>
                    {/* <S.RadioContainer>
                      <Radio value="CREDIT_CARD" />
                      <S.RadioNameContainer>
                        <BsFillCreditCard2BackFill />
                        Cartão de Crédito
                      </S.RadioNameContainer>
                    </S.RadioContainer>
                    <S.RadioContainer>
                      <Radio value="PIX" />
                      <S.RadioNameContainer>
                        <FaPix />
                        Pix
                      </S.RadioNameContainer>
                    </S.RadioContainer> */}
                    <S.TermServicesContainer>
                      <small>
                        Ao Clicar em "Concluir Cadastro" Você Concorda Com os{" "}
                        <span onClick={() => setIsTermsServicesOpen(true)}>
                          Termos de Serviços
                        </span>
                      </small>
                    </S.TermServicesContainer>
                    <FutButton
                      type="submit"
                      isLoading={isLoading}
                      fullWidth
                      sx={{ mt: 3 }}
                    >
                      Concluir Cadastro
                    </FutButton>
                  </RadioGroup>
                )}
              />
            </form>
          </S.RegisterContainer>
        </S.RegisterWrapper>
      ),
    },
  ];

  return (
    <>
      <S.Navigation>
        <img src={logo} alt="Futscore" />
        <S.LoginButton variant="outlined" onClick={() => navigate("/login")}>
          Entrar
        </S.LoginButton>
      </S.Navigation>
      <S.BackgroundVector />
      <S.Container ref={containerRef}>
        {registerParts[step].components}
      </S.Container>
      <Modal open={isModalOpen}>
        <S.ModalContainer>
          {isLoading ? (
            <>
              <S.LoadingContainer>
                <CircularProgress />
              </S.LoadingContainer>
              <h2>Finalizando cadastro aguarde...</h2>
            </>
          ) : (
            <>
              <S.LoadingContainer>
                <SuccessSvg />
              </S.LoadingContainer>
              <h2>
                Cadastro realizado com sucesso! <br /> <br /> Em instantes você
                será redirecionado para a área de pagamento...
              </h2>
            </>
          )}
        </S.ModalContainer>
      </Modal>
      <Modal
        onClose={() => setIsTermsServicesOpen(false)}
        open={isTermServicesOpen}
      >
        <S.ModalWrapper>
          <S.TermServicesModalCont>
            <S.TermsHeader>
              <span onClick={() => setIsTermsServicesOpen(false)}>
                <IoClose />
              </span>
            </S.TermsHeader>
            <S.TermsTextCont>
              <p>
                Ao utilizar os serviços oferecidos por Futscore, você concorda com
                os seguintes termos e condições:
              </p>

              <h2>1. Assinaturas e Pagamentos</h2>

              <p>
                1.1. Ao se inscrever para assinaturas mensais, semestrais ou
                anuais em nossa plataforma, você concorda em fornecer
                informações de pagamento precisas, atualizadas e completas. Caso
                haja qualquer alteração nos dados de pagamento, compromete-se a
                atualizá-los imediatamente.
              </p>
              <p>
                1.2. Aceitamos pagamentos via PIX e cartões de crédito. Ao
                efetuar um pagamento, você autoriza de forma irrestrita e
                incondicional a cobrança do valor correspondente.
              </p>

              <h2>2. Programa de Afiliados</h2>

              <p>
                2.1. Nossa plataforma oferece a oportunidade de se tornar
                afiliado para a venda de assinaturas mensais.
              </p>
              <p>
                2.2. Os afiliados são remunerados de acordo com os termos
                estabelecidos em nosso programa de afiliados. Entende-se que as
                porcentagens de comissionamento estão sujeitas a mudanças sem
                aviso prévio, sendo atualizadas periodicamente de acordo com
                critérios internos.
              </p>
              <p>
                2.3. A plataforma reserva o direito de alterar as condições do
                programa de afiliados a qualquer momento, sem aviso prévio, e as
                alterações serão efetivas imediatamente após a publicação em
                nosso site.
              </p>

              <h2>3. Saques</h2>

              <p>
                3.1. Os usuários têm o direito de sacar os valores referentes às
                suas atividades na plataforma. No entanto, a plataforma se
                reserva o direito de revisar e validar todas as transações antes
                de processar os saques, a fim de evitar atividades fraudulentas
                ou não autorizadas.
              </p>
              <p>
                3.2. Os saques e as condições relacionadas estão sujeitos a
                mudanças sem aviso prévio, e as alterações serão publicadas em
                nosso site.
              </p>

              <h2>4. Métricas Esportivas e Palpites</h2>

              <p>
                4.1. Nosso compromisso é fornecer informações precisas sobre
                métricas esportivas e palpites. No entanto, não garantimos
                resultados específicos, e todas as decisões tomadas com base
                nessas informações são de responsabilidade exclusiva do usuário.
              </p>
              <p>
                4.2. A plataforma não se responsabiliza por perdas ou danos
                decorrentes da utilização das informações fornecidas.
              </p>

              <h2>5. Estorno e Política de Cancelamento</h2>

              <p>
                5.1. De acordo com a legislação brasileira, é possível solicitar
                o estorno de pagamentos dentro de um período de 7 dias a partir
                da data da assinatura.
              </p>
              <p>
                5.2. Após esse período, as assinaturas são consideradas não
                reembolsáveis, salvo disposição contrária expressa em acordos
                específicos. Ressaltamos que as condições de estorno e política
                de cancelamento estão sujeitas a alterações sem aviso prévio, e
                as atualizações serão comunicadas em nosso site.
              </p>

              <h2>6. Proteção de Dados (LGPD)</h2>

              <p>
                6.1. Ao utilizar nossos serviços, você concorda que podemos
                coletar, processar e utilizar seus dados pessoais de acordo com
                nossa Política de Privacidade.
              </p>
              <p>
                6.2. Respeitamos a privacidade dos usuários e comprometemo-nos a
                proteger suas informações pessoais de acordo com as disposições
                da Lei Geral de Proteção de Dados.
              </p>
              <p>
                6.3. Você reconhece que, ao concordar com estes termos,
                concede-nos permissão para utilizar seus dados para os fins
                operacionais da plataforma, respeitando sempre as normas de
                privacidade estabelecidas pela legislação aplicável.
              </p>

              <h2>7. Consentimento para Uso de Dados</h2>

              <p>
                7.1. Você concorda que podemos utilizar seus dados pessoais para
                fins de operação da plataforma, incluindo marketing e
                comunicação.
              </p>
              <p>
                7.2. Respeitamos sua privacidade, e as informações coletadas
                serão tratadas de acordo com nossa Política de Privacidade.
              </p>

              <p>
                Ao continuar a usar nossa plataforma, você confirma que leu,
                compreendeu e concorda com estes termos e condições. Estes
                termos estão sujeitos a alterações, e recomendamos que os
                usuários revisem periodicamente para se manterem informados
                sobre quaisquer atualizações.
              </p>

              <p>
                Lembre-se de que este é um exemplo geral e é altamente
                recomendável consultar um advogado para garantir que esses
                termos estejam em conformidade com a legislação vigente e
                protejam adequadamente os interesses da sua empresa.
              </p>
            </S.TermsTextCont>
          </S.TermServicesModalCont>
        </S.ModalWrapper>
      </Modal>
    </>
  );
}
